import { useState, FormEvent, useEffect } from "react";

import { tracking } from "@iwoca/frontend-tracking-library";
import {
  fetchPostIwocapayNotifications,
  fetchPostPasswordlessEmail,
} from "@iwoca/lapi-client/edge";
import { LapiError } from "@iwoca/lapi-client/lendingAPI";
import { AlertBox, Button, Input, Label } from "@iwoca/orion";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useLocation, useNavigate, Link } from "react-router";

import { getAuthServiceRedirectTo, getRedirectToLink } from "./getRedirectLink";
import greenCheckSVG from "../../assets/checkmark.svg";
import { usePost } from "../../hooks/usePost";
import { LoginErrorMessage } from "../../lib/LoginErrorMessage/LoginErrorMessage";
import { getRuntimeEnvironment } from "../../utils/getRuntimeEnvironment";
import logger from "../../utils/logger";
import { initiateMagicLink as authServiceInitiateMagicLink } from "../auth-service-client";
import styles from "../CommonLogin.module.css";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import LoginContainer from "../LoginContainer";
import { isValidEmail } from "../LoginHelpers";
import {
  determineTwoFactorRedirect,
  reportIwocaPayBrokenAccount,
} from "../MultifactorAuth/utils/ErrorHandling";
import { determineAuthFlow } from "../utils/checkEmailExists";

type LoginWithLinkRouterState = {
  redirectReason?: string;
  autoInitiate?: boolean;
};

// Must be at least MIN_WAIT_SECONDS in cognito-infrastructure's create-auth-challenge lambda, which drops an earlier resend and still answers 200.
const RESEND_COOLDOWN_MS = 60_000;

const LoginWithLink = () => {
  const { search, state } = useLocation();
  const routerState = (state ?? {}) as LoginWithLinkRouterState;
  const isCognitoForcedRedirect =
    routerState.redirectReason === "cognito_forced_passwordless";
  const shouldAutoInitiate =
    isCognitoForcedRedirect && routerState.autoInitiate === true;
  const initialEmail = new URLSearchParams(search).get("email") || "";
  const [email, setEmail] = useState(initialEmail);
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const { post: submitNotification } = usePost(fetchPostIwocapayNotifications);
  const navigate = useNavigate();
  const { post: login, isLoading, error } = usePost(fetchPostPasswordlessEmail);
  const [isCognitoLoading, setIsCognitoLoading] = useState(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [hasCognitoError, setHasCognitoError] = useState<boolean>(false);
  const [isInResendCooldown, setIsInResendCooldown] = useState<boolean>(false);
  const isSubmitting = isLoading || isCognitoLoading;
  const searchParams = new URLSearchParams(search);
  const isCreditCompassRedirect = searchParams
    .get("redirect_to")
    ?.includes("/credit-compass");
  const isNewAuthFlow = searchParams.get("flow") === "new";

  useEffect(() => {
    if (!shouldAutoInitiate || !isValidEmail(initialEmail)) {
      return;
    }
    let cancelled = false;
    (async () => {
      setIsCognitoLoading(true);
      try {
        await authServiceInitiateMagicLink({
          email: initialEmail,
          redirect_to: getAuthServiceRedirectTo(),
        });
        if (!cancelled) {
          setSuccess(true);
          setIsInResendCooldown(true);
        }
      } catch (authServiceError: any) {
        logger.error("Auth-service auto-initiate magic link failed", {
          error: authServiceError?.message || "Unknown error",
          code: authServiceError?.code,
        });
        if (!cancelled) setHasCognitoError(true);
      } finally {
        if (!cancelled) setIsCognitoLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isInResendCooldown) {
      return;
    }
    const timer = setTimeout(
      () => setIsInResendCooldown(false),
      RESEND_COOLDOWN_MS,
    );
    return () => clearTimeout(timer);
  }, [isInResendCooldown]);

  // Amplitude tracking
  const [visited, setVisited] = useState<boolean>(false);
  const [emailEntered, setEmailEntered] = useState<boolean>(false);
  if (!visited) {
    tracking.ampli.passwordlessLoginVisited();
    setVisited(true);
  }
  useEffect(() => {
    if (email && !emailEntered) {
      tracking.ampli.loginEmailEntered();
      setEmailEntered(true);
    }
  }, [email]);

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();

    // Ignore whitespace pasted around the address rather than rejecting it.
    const trimmedEmail = email.trim();
    const isEmailValid = isValidEmail(trimmedEmail);
    if (!isEmailValid) {
      setIsEmailInvalid(true);
    }

    if (isEmailValid) {
      tracking.ampli.passwordlessLoginRequested();
      setHasCognitoError(false);

      try {
        // Smart routing: Check which auth system the email belongs to
        const authFlow = isNewAuthFlow
          ? "auth-service"
          : await determineAuthFlow(trimmedEmail);

        if (authFlow === "django") {
          // User exists in Django - use existing Django magic link flow
          try {
            await login({
              data: { email: trimmedEmail, redirect_to: getRedirectToLink() },
            });
            setSuccess(true);
          } catch (e) {
            const lapiError = e as LapiError;
            // Handle Django errors
            reportIwocaPayBrokenAccount({
              lapiError,
              email: trimmedEmail,
              submitNotification,
            });
            determineTwoFactorRedirect({
              lapiError,
              shouldRedirect: (shouldRedirectToUrl) => {
                if (shouldRedirectToUrl === null) {
                  tracking.ampli.loginError();
                } else {
                  navigate(shouldRedirectToUrl);
                }
              },
            });
          }
        } else {
          try {
            setIsCognitoLoading(true);
            await authServiceInitiateMagicLink({
              email: trimmedEmail,
              redirect_to: getAuthServiceRedirectTo(),
            });
            setSuccess(true);
            setIsInResendCooldown(true);
          } catch (authServiceError: any) {
            logger.error("Auth-service magic link initiation failed", {
              error: authServiceError?.message || "Unknown error",
              code: authServiceError?.code,
            });
            setHasCognitoError(true);
            tracking.ampli.loginError();
          } finally {
            setIsCognitoLoading(false);
          }
        }
      } catch (error: any) {
        logger.error("Auth flow determination failed", {
          error: error?.message || "Unknown error",
        });
        setHasCognitoError(true);
        tracking.ampli.loginError();
      }
    }
  };

  return (
    <LoginContainer>
      <form
        className={styles.loginWithLinkForm}
        onSubmit={handleLogin}
        data-testid="login-link-form"
      >
        {isCognitoForcedRedirect ? (
          <AlertBox
            variant="connect"
            className={styles.errorBox}
            data-testid="cognito-redirect-notice"
          >
            <Trans>
              We've updated how you sign in. We've sent you a login link - check
              your inbox to log in.
            </Trans>
          </AlertBox>
        ) : null}
        <Label htmlFor="email-input" className={styles.loginLabel}>
          <Trans>Email</Trans>
        </Label>
        <Input
          className={`${styles.fieldInputs} fs-exclude`}
          data-testid="email-input"
          placeholder="name@provider.com"
          id="email-input"
          value={email}
          onChange={(event) => {
            setIsEmailInvalid(false);
            setEmail(event.target.value);
          }}
          onBlur={() => {
            if (!isValidEmail(email.trim())) {
              setIsEmailInvalid(true);
            }
          }}
        />
        {isEmailInvalid ? (
          <AlertBox
            variant="failure"
            className={styles.errorBox}
            data-testid="error-message"
          >
            <Trans>Are you sure? This is not a valid email address</Trans>
          </AlertBox>
        ) : null}

        <Button
          disabled={isEmailInvalid || isSubmitting || isInResendCooldown}
          type="submit"
          variant="primary"
          className={styles.submitButton}
        >
          {isSubmitting ? (
            <LoadingSpinner data-testid="confirm-button-loading" />
          ) : success ? (
            <Trans>Retry</Trans>
          ) : (
            <Trans>Send me a login link</Trans>
          )}
        </Button>

        {success ? (
          <AlertBox
            variant="success"
            className={styles.goodBox}
            data-testid="success-message"
          >
            <img src={greenCheckSVG} className={styles.checkmark} alt="" />
            <Trans>Check your email inbox; we've sent a link</Trans>
          </AlertBox>
        ) : null}

        {hasCognitoError ? (
          <AlertBox
            variant="failure"
            className={styles.errorBox}
            data-testid="cognito-error-message"
          >
            <Trans>
              Sorry about that; we're looking into it. Try again later?
            </Trans>
          </AlertBox>
        ) : null}

        {error ? (
          <LoginErrorMessage
            error={error}
            copy={{
              loginEmailOrPassWrong: t`The email or password you entered is incorrect`,
              loginError: t`Sorry about that; we're looking into it. Try again later?`,
            }}
          />
        ) : null}

        {!isCreditCompassRedirect && !isNewAuthFlow && (
          <>
            <span className={styles.border}></span>
            <span className={styles.linkCopy} data-testid="link-to-password">
              <Trans>
                Or{" "}
                <Link
                  className={styles.linkButton}
                  to={`/login/password${search}`}
                >
                  login with password
                </Link>{" "}
                instead.
              </Trans>
            </span>
          </>
        )}
      </form>
    </LoginContainer>
  );
};

export default LoginWithLink;
